// The conversation box: the speaker's name, the line as it types, the choices (with their tone) and a text field for
// free text, drawn from the feature's view each frame (written straight to the elements, as the HUD's values; decision
// 0046). Clicks and Enter dispatch the feature's actions through the game's `dispatch`. Restyle `.rpg-talk`.
import type { ConversationFeature, ConversationView } from "./index";

/** The box's default look (a game overrides `.rpg-talk` rules). */
export const CONVERSATION_CSS = `
.rpg-talk{position:fixed;left:50%;bottom:max(18px,env(safe-area-inset-bottom));transform:translateX(-50%);width:min(92vw,640px);
  background:rgba(20,18,28,.88);color:#f4efe6;border-radius:12px;padding:14px 18px;font:16px/1.45 system-ui,sans-serif;z-index:30}
.rpg-talk[hidden]{display:none}
.rpg-talk .rpg-talk-name{font-weight:600;opacity:.85;margin-bottom:4px}
.rpg-talk .rpg-talk-line{min-height:2.9em}
.rpg-talk .rpg-talk-choices{display:flex;flex-direction:column;gap:6px;margin-top:8px}
.rpg-talk button{font:inherit;text-align:left;background:rgba(255,255,255,.08);color:inherit;border:1px solid rgba(255,255,255,.18);border-radius:8px;padding:6px 10px;cursor:pointer}
.rpg-talk .rpg-talk-tone{opacity:.6;font-size:.85em;margin-left:.5em}
.rpg-talk input{font:inherit;width:100%;box-sizing:border-box;margin-top:8px;padding:6px 10px;border-radius:8px;border:1px solid rgba(255,255,255,.18);background:rgba(0,0,0,.25);color:inherit}
`;

/** Mount the box; call `update()` each frame. `freeText` shows the text field while choices show. */
export function mountConversationBox(o: { feature: ConversationFeature; dispatch(action: string, payload?: unknown): void; freeText?: boolean; doc?: Document; parent?: HTMLElement }) {
  const doc = o.doc ?? document;
  if (!doc.getElementById("rpg-talk-css")) {
    const s = doc.createElement("style");
    s.id = "rpg-talk-css";
    s.textContent = CONVERSATION_CSS;
    doc.head.appendChild(s);
  }
  const box = doc.createElement("div");
  box.className = "rpg-talk";
  box.setAttribute("role", "dialog");
  box.hidden = true;
  const name = doc.createElement("div");
  name.className = "rpg-talk-name";
  const line = doc.createElement("div");
  line.className = "rpg-talk-line";
  line.setAttribute("aria-live", "polite");
  const choices = doc.createElement("div");
  choices.className = "rpg-talk-choices";
  const input = doc.createElement("input");
  input.placeholder = "…";
  input.addEventListener("keydown", (e) => {
    if (e.key === "Enter" && input.value.trim()) {
      o.dispatch(o.feature.actions.say, { text: input.value });
      input.value = "";
    }
  });
  box.append(name, line, choices, input);
  box.addEventListener("click", (e) => {
    const b = (e.target as HTMLElement).closest("button");
    if (b?.dataset.n !== undefined) o.dispatch(o.feature.actions.choose, { n: Number(b.dataset.n) });
    else if (e.target !== input) o.dispatch(o.feature.actions.advance);
  });
  (o.parent ?? doc.body).appendChild(box);
  let last = "";
  return {
    el: box,
    /** Draw the view now. */
    update(): ConversationView | null {
      const v = o.feature.view();
      box.hidden = v === null || v.waiting;
      if (!v || v.waiting) return v;
      name.textContent = v.speakerName ?? v.name;
      line.textContent = v.text !== null ? v.shown : v.thinking ? "…" : "";
      const key = JSON.stringify(v.choices);
      if (key !== last) {
        last = key;
        choices.replaceChildren(
          ...v.choices.map((c) => {
            const b = doc.createElement("button");
            b.dataset.n = String(c.n);
            b.textContent = c.text;
            if (c.tone) {
              const t = doc.createElement("span");
              t.className = "rpg-talk-tone";
              t.textContent = c.tone;
              b.appendChild(t);
            }
            return b;
          }),
        );
      }
      // an open talk takes typed words on the player's turn; a static one when the game allows free text
      input.hidden = !((v.open && v.text === null) || (o.freeText && v.choices.length));
      return v;
    },
    remove(): void {
      box.remove();
    },
  };
}
